import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import {
  collection,
  doc,
  setDoc,
  onSnapshot,
  updateDoc,
  deleteDoc,
  writeBatch,
} from 'firebase/firestore';
import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { db, storage } from '../lib/firebase';
import { enqueueUpload, getPendingUploads, dequeueUpload, type PendingUpload } from '../lib/uploadQueue';
import { enqueueWrite, getPendingWrites, dequeueWrite, type WriteOp, type PendingWrite } from '../lib/writeQueue';
import { useAuth } from './AuthContext';
import { usePet } from './PetContext';
import { useToast } from '../hooks/useToast';
import { DailyLog, Medication, Contact, DocumentCategory, VaultDocument } from '../types';

interface DataContextType {
  logs: DailyLog[];
  medications: Medication[];
  contacts: Contact[];
  documents: VaultDocument[];
  addLog: (log: Omit<DailyLog, 'id'>) => Promise<void>;
  updateLog: (id: string, updates: Partial<DailyLog>) => Promise<void>;
  addMedication: (med: Omit<Medication, 'id'>) => Promise<void>;
  updateMedication: (id: string, updates: Partial<Medication>) => Promise<void>;
  addContact: (contact: Omit<Contact, 'id'>) => Promise<void>;
  updateContact: (id: string, updates: Omit<Contact, 'id'>) => Promise<void>;
  deleteMedication: (id: string) => Promise<void>;
  deleteContact: (id: string) => Promise<void>;
  deleteLog: (id: string) => Promise<void>;
  deleteLogs: (ids: string[]) => Promise<void>;
  uploadDocument: (payload: {
    file: File;
    category: DocumentCategory;
    title?: string;
    note?: string;
    docDate?: string;
  }) => Promise<void>;
  deleteDocument: (docMeta: VaultDocument) => Promise<void>;
  pendingUploadCount: number;
  pendingWriteCount: number;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

export function DataProvider({ children }: { children: React.ReactNode }) {
  // Use firebaseUser.uid as the gate for Firestore subscriptions and writes.
  // This is available immediately from the cached Firebase Auth token — it does
  // NOT wait for the Firestore profile fetch (user), which can time out on slow
  // mobile connections and would otherwise leave data subscriptions never set up.
  const { firebaseUser } = useAuth();
  const uid = firebaseUser?.uid ?? null;

  // All data is scoped to the active pet. petBase is the Firestore collection
  // root for the current pet: `users/{ownerUid}/pets/{petId}`.
  // For shared pets, activePetOwnerUid is the original owner's uid (not the current user's).
  const { activePetId, activePetOwnerUid } = usePet();
  const petBase = activePetOwnerUid && activePetId ? `users/${activePetOwnerUid}/pets/${activePetId}` : null;
  const isSubordinate = Boolean(uid && activePetOwnerUid && uid !== activePetOwnerUid);

  const { showToast, ToastMount } = useToast();

  const [logs, setLogs] = useState<DailyLog[]>([]);
  const [medications, setMedications] = useState<Medication[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [documents, setDocuments] = useState<VaultDocument[]>([]);
  const [pendingUploadCount, setPendingUploadCount] = useState(0);
  const [pendingWriteCount, setPendingWriteCount] = useState(0);

  // Load any pending operations that survived a previous session.
  useEffect(() => {
    getPendingUploads().then(q => setPendingUploadCount(q.length)).catch(() => {});
    getPendingWrites().then(q => setPendingWriteCount(q.length)).catch(() => {});
  }, []);

  // Subscribe to Firestore collections scoped to the active pet.
  // persistentLocalCache (set in firebase.ts) means onSnapshot serves from
  // IndexedDB immediately on load — even when offline — then reconciles
  // with the server when connectivity is restored.
  useEffect(() => {
    if (!petBase) {
      setLogs([]);
      setMedications([]);
      setContacts([]);
      setDocuments([]);
      return;
    }

    // Logs use a two-level subscription:
    //   1. Watch {petBase}/logs for date container documents
    //   2. For each date, watch {petBase}/logs/{date}/entries
    const accumulated = new Map<string, DailyLog[]>();
    const entrySubs  = new Map<string, () => void>();

    const unsubDates = onSnapshot(
      collection(db, petBase, 'logs'),
      datesSnap => {
        const currentDates = new Set(datesSnap.docs.map(d => d.id));

        for (const [dateStr, fn] of entrySubs) {
          if (!currentDates.has(dateStr)) {
            fn();
            entrySubs.delete(dateStr);
            accumulated.delete(dateStr);
          }
        }

        for (const dateDoc of datesSnap.docs) {
          const dateStr = dateDoc.id;
          if (entrySubs.has(dateStr)) continue;

          const unsubEntries = onSnapshot(
            collection(db, petBase, 'logs', dateStr, 'entries'),
            entriesSnap => {
              accumulated.set(
                dateStr,
                entriesSnap.docs.map(d => ({ id: `${dateStr}/${d.id}`, ...d.data() } as DailyLog))
              );
              setLogs(Array.from(accumulated.values()).flat());
            },
            (error) => {
              console.error(`[DataContext] Failed to read ${petBase}/logs/${dateStr}/entries:`, error);
            }
          );
          entrySubs.set(dateStr, unsubEntries);
        }

        if (datesSnap.empty) setLogs([]);
      },
      (error) => {
        console.error('[DataContext] Failed to read logs:', error);
      }
    );

    const unsubMeds = onSnapshot(
      collection(db, petBase, 'medications'),
      snap => setMedications(snap.docs.map(d => ({ id: d.id, ...d.data() } as Medication))),
      (error) => {
        console.error('[DataContext] Failed to read medications:', error);
      }
    );

    const unsubContacts = onSnapshot(
      collection(db, petBase, 'contacts'),
      snap => setContacts(snap.docs.map(d => ({ id: d.id, ...d.data() } as Contact))),
      (error) => {
        console.error('[DataContext] Failed to read contacts:', error);
      }
    );

    const unsubDocuments = onSnapshot(
      collection(db, petBase, 'documents'),
      snap => {
        const docs = snap.docs
          .map(d => ({ id: d.id, ...d.data() } as VaultDocument))
          .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
        setDocuments(docs);
      },
      (error) => {
        console.error('[DataContext] Failed to read documents:', error);
      }
    );

    return () => {
      unsubDates();
      entrySubs.forEach(fn => fn());
      unsubMeds();
      unsubContacts();
      unsubDocuments();
    };
  }, [petBase]);

  // ── Offline upload retry ────────────────────────────────────────────────────

  /**
   * Drains the IndexedDB upload queue, retrying each entry.
   * Called on the `online` window event and when the service worker sends
   * a `FLUSH_UPLOAD_QUEUE` message (Background Sync API — Chrome/Edge).
   * Skips entries that belong to a different user or pet context.
   */
  const retryPendingUploads = useCallback(async () => {
    if (!uid || !petBase) return;
    let items: PendingUpload[];
    try { items = await getPendingUploads(); } catch { return; }
    if (!items.length) return;

    for (const item of items) {
      if (item.petBase !== petBase || item.uid !== uid) continue;
      try {
        const storageRef = ref(storage, item.storagePath);
        await uploadBytes(storageRef, item.buffer, { contentType: item.mimeType });
        const downloadUrl = await getDownloadURL(storageRef);
        await setDoc(doc(db, item.petBase, 'documents', item.firestoreDocId), {
          user_id:      item.uid,
          category:     item.category,
          file_name:    item.fileName,
          file_size:    item.fileSize,
          mime_type:    item.mimeType,
          storage_path: item.storagePath,
          download_url: downloadUrl,
          title:        item.title,
          note:         item.note,
          doc_date:     item.docDate,
          created_at:   new Date(item.enqueuedAt).toISOString(),
        });
        await dequeueUpload(item.id);
        // Reflect in local state — remove any placeholder and add the real doc.
        const recovered: VaultDocument = {
          id:           item.firestoreDocId,
          user_id:      item.uid,
          category:     item.category as DocumentCategory,
          file_name:    item.fileName,
          file_size:    item.fileSize,
          mime_type:    item.mimeType,
          storage_path: item.storagePath,
          download_url: downloadUrl,
          title:        item.title ?? undefined,
          note:         item.note ?? undefined,
          doc_date:     item.docDate ?? undefined,
          created_at:   new Date(item.enqueuedAt).toISOString(),
        };
        setDocuments(prev =>
          [recovered, ...prev.filter(d => d.id !== item.firestoreDocId)]
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        );
        setPendingUploadCount(prev => Math.max(0, prev - 1));
        showToast(`"${item.fileName}" uploaded successfully.`, 'success');
      } catch (err) {
        console.warn('[DataContext] retryPendingUploads: item still failing, will retry later:', err);
      }
    }
  }, [uid, petBase, showToast]);

  // Listen for connectivity restoration (all browsers) and Background Sync
  // messages from the service worker (Chrome/Edge).
  useEffect(() => {
    const handleOnline = () => retryPendingUploads();
    window.addEventListener('online', handleOnline);

    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'FLUSH_UPLOAD_QUEUE') retryPendingUploads();
    };
    navigator.serviceWorker?.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('online', handleOnline);
      navigator.serviceWorker?.removeEventListener('message', handleMessage);
    };
  }, [retryPendingUploads]);

  // ── Firestore write retry queue ──────────────────────────────────────────────

  /**
   * Replays a single serialised Firestore write op.
   * `doc(db, path)` accepts slash-separated paths for documents.
   */
  const replayWrite = useCallback(async (op: WriteOp): Promise<void> => {
    const ref = doc(db, op.path);
    if (op.type === 'set')    { await setDoc(ref, op.data, op.merge ? { merge: true } : {}); return; }
    if (op.type === 'update') { await updateDoc(ref, op.data); return; }
    if (op.type === 'delete') { await deleteDoc(ref); }
  }, []);

  /**
   * Drains the write queue, retrying each Firestore op.
   * Called on the `online` event and on `FLUSH_WRITE_QUEUE` SW message.
   */
  const retryPendingWrites = useCallback(async () => {
    let items: PendingWrite[];
    try { items = await getPendingWrites(); } catch { return; }
    if (!items.length) return;
    for (const item of items) {
      try {
        await replayWrite(item.op);
        await dequeueWrite(item.id);
        setPendingWriteCount(prev => Math.max(0, prev - 1));
      } catch (err) {
        console.warn('[DataContext] retryPendingWrites: write still failing, will retry later:', err);
      }
    }
  }, [replayWrite]);

  useEffect(() => {
    const handleOnline = () => retryPendingWrites();
    window.addEventListener('online', handleOnline);
    const handleMessage = (e: MessageEvent) => {
      if (e.data?.type === 'FLUSH_WRITE_QUEUE') retryPendingWrites();
    };
    navigator.serviceWorker?.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('online', handleOnline);
      navigator.serviceWorker?.removeEventListener('message', handleMessage);
    };
  }, [retryPendingWrites]);

  /**
   * Enqueues Firestore write ops for offline retry.
   * Also registers the `koda-write-retry` Background Sync tag (Chrome/Edge)
   * so the SW can wake the app after connectivity is restored.
   */
  const enqueueWrites = useCallback((writes: Array<{ id: string; op: WriteOp }>) => {
    for (const w of writes) {
      enqueueWrite({ id: w.id, op: w.op, enqueuedAt: Date.now() }).catch(() => {});
    }
    setPendingWriteCount(c => c + writes.length);
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready
        .then(reg => { if ('sync' in reg) (reg as any).sync.register('koda-write-retry').catch(() => {}); })
        .catch(() => {});
    }
  }, []);

  // ── Logs ────────────────────────────────────────────────────────────────────

  const stripUndefined = <T extends object>(obj: T): Partial<T> =>
    Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as Partial<T>;

  const addLog = useCallback(async (log: Omit<DailyLog, 'id'>) => {
    if (!petBase) return;
    const dateStr = log.timestamp.split('T')[0];

    // Pre-generate the Firestore doc ref so we know the ID before writing.
    const entryRef = doc(collection(db, petBase, 'logs', dateStr, 'entries'));
    const optimisticId = `${dateStr}/${entryRef.id}`;
    const optimisticEntry = { id: optimisticId, ...log } as DailyLog;

    // Instant local update — UI reflects the new entry immediately.
    setLogs(prev => [...prev, optimisticEntry]);

    // Background sync — fire-and-forget; onSnapshot will confirm or reconcile.
    setDoc(doc(db, petBase, 'logs', dateStr), { date: dateStr }, { merge: true })
      .then(() => setDoc(entryRef, stripUndefined(log)))
      .catch(err => {
        console.error('[DataContext] addLog failed:', err);
        if (!navigator.onLine) {
          enqueueWrites([
            { id: `date:${petBase}/logs/${dateStr}`, op: { type: 'set', path: `${petBase}/logs/${dateStr}`, data: { date: dateStr }, merge: true } },
            { id: optimisticId, op: { type: 'set', path: entryRef.path, data: stripUndefined(log) as Record<string, unknown> } },
          ]);
        } else {
          setLogs(prev => prev.filter(l => l.id !== optimisticId));
          showToast('Could not save log. Please try again.', 'error');
        }
      });
  }, [petBase, enqueueWrites, showToast]);

  const updateLog = useCallback(async (id: string, updates: Partial<DailyLog>) => {
    if (!petBase) return;
    const slashIdx = id.indexOf('/');
    const dateStr = id.slice(0, slashIdx);
    const entryId = id.slice(slashIdx + 1);

    // Capture previous value for rollback.
    let previous: DailyLog | undefined;
    setLogs(prev => {
      previous = prev.find(l => l.id === id);
      return prev.map(l => l.id === id ? { ...l, ...updates } : l);
    });

    updateDoc(doc(db, petBase, 'logs', dateStr, 'entries', entryId), stripUndefined(updates))
      .catch(err => {
        console.error('[DataContext] updateLog failed:', err);
        if (!navigator.onLine) {
          enqueueWrites([{ id: id, op: { type: 'update', path: `${petBase}/logs/${dateStr}/entries/${entryId}`, data: stripUndefined(updates) as Record<string, unknown> } }]);
        } else {
          if (previous) setLogs(prev => prev.map(l => l.id === id ? previous! : l));
          showToast('Could not update log. Please try again.', 'error');
        }
      });
  }, [petBase, enqueueWrites, showToast]);

  const deleteLog = useCallback(async (id: string) => {
    if (!petBase) return;
    const slashIdx = id.indexOf('/');
    const dateStr = id.slice(0, slashIdx);
    const entryId = id.slice(slashIdx + 1);

    let removed: DailyLog | undefined;
    setLogs(prev => {
      removed = prev.find(l => l.id === id);
      return prev.filter(l => l.id !== id);
    });

    deleteDoc(doc(db, petBase, 'logs', dateStr, 'entries', entryId))
      .catch(err => {
        console.error('[DataContext] deleteLog failed:', err);
        if (!navigator.onLine) {
          enqueueWrites([{ id: `del:${id}`, op: { type: 'delete', path: `${petBase}/logs/${dateStr}/entries/${entryId}` } }]);
        } else {
          if (removed) setLogs(prev => [...prev, removed!]);
          showToast('Could not delete log. Please try again.', 'error');
        }
      });
  }, [petBase, enqueueWrites, showToast]);

  const deleteLogs = useCallback(async (ids: string[]) => {
    if (!petBase) return;

    let removed: DailyLog[] = [];
    setLogs(prev => {
      removed = prev.filter(l => ids.includes(l.id));
      return prev.filter(l => !ids.includes(l.id));
    });

    const batch = writeBatch(db);
    ids.forEach(id => {
      const slashIdx = id.indexOf('/');
      const dateStr = id.slice(0, slashIdx);
      const entryId = id.slice(slashIdx + 1);
      batch.delete(doc(db, petBase, 'logs', dateStr, 'entries', entryId));
    });
    batch.commit().catch(err => {
      console.error('[DataContext] deleteLogs failed:', err);
      if (!navigator.onLine) {
        enqueueWrites(ids.map(id => {
          const si = id.indexOf('/');
          const ds = id.slice(0, si);
          const ei = id.slice(si + 1);
          return { id: `del:${id}`, op: { type: 'delete' as const, path: `${petBase}/logs/${ds}/entries/${ei}` } };
        }));
      } else {
        if (removed.length) setLogs(prev => [...prev, ...removed]);
        showToast('Could not delete logs. Please try again.', 'error');
      }
    });
  }, [petBase, enqueueWrites, showToast]);

  // ── Medications ─────────────────────────────────────────────────────────────

  const addMedication = useCallback(async (med: Omit<Medication, 'id'>) => {
    if (isSubordinate) {
      showToast('Only the pet owner can edit medications for this pet.', 'error');
      return;
    }
    if (!petBase) return;
    const medRef = doc(collection(db, petBase, 'medications'));
    const optimistic = { id: medRef.id, ...med } as Medication;

    setMedications(prev => [...prev, optimistic]);

    setDoc(medRef, stripUndefined(med)).catch(err => {
      console.error('[DataContext] addMedication failed:', err);
      if (!navigator.onLine) {
        enqueueWrites([{ id: medRef.id, op: { type: 'set', path: medRef.path, data: stripUndefined(med) as Record<string, unknown> } }]);
      } else {
        setMedications(prev => prev.filter(m => m.id !== medRef.id));
        showToast('Could not save medication. Please try again.', 'error');
      }
    });
  }, [isSubordinate, petBase, enqueueWrites, showToast]);

  const updateMedication = useCallback(async (id: string, updates: Partial<Medication>) => {
    if (isSubordinate) {
      showToast('Only the pet owner can edit medications for this pet.', 'error');
      return;
    }
    if (!petBase) return;
    let previous: Medication | undefined;
    setMedications(prev => {
      previous = prev.find(m => m.id === id);
      return prev.map(m => m.id === id ? { ...m, ...updates } : m);
    });

    updateDoc(doc(db, petBase, 'medications', id), stripUndefined(updates)).catch(err => {
      console.error('[DataContext] updateMedication failed:', err);
      if (!navigator.onLine) {
        enqueueWrites([{ id: `upd:${petBase}/medications/${id}`, op: { type: 'update', path: `${petBase}/medications/${id}`, data: stripUndefined(updates) as Record<string, unknown> } }]);
      } else {
        if (previous) setMedications(prev => prev.map(m => m.id === id ? previous! : m));
        showToast('Could not update medication. Please try again.', 'error');
      }
    });
  }, [isSubordinate, petBase, enqueueWrites, showToast]);

  const deleteMedication = useCallback(async (id: string) => {
    if (isSubordinate) {
      showToast('Only the pet owner can edit medications for this pet.', 'error');
      return;
    }
    if (!petBase) return;
    let removed: Medication | undefined;
    setMedications(prev => {
      removed = prev.find(m => m.id === id);
      return prev.filter(m => m.id !== id);
    });

    deleteDoc(doc(db, petBase, 'medications', id)).catch(err => {
      console.error('[DataContext] deleteMedication failed:', err);
      if (!navigator.onLine) {
        enqueueWrites([{ id: `del:${petBase}/medications/${id}`, op: { type: 'delete', path: `${petBase}/medications/${id}` } }]);
      } else {
        if (removed) setMedications(prev => [...prev, removed!]);
        showToast('Could not delete medication. Please try again.', 'error');
      }
    });
  }, [isSubordinate, petBase, enqueueWrites, showToast]);

  // ── Contacts ─────────────────────────────────────────────────────────────────

  const addContact = useCallback(async (contact: Omit<Contact, 'id'>) => {
    if (isSubordinate) {
      showToast('Only the pet owner can edit contacts for this pet.', 'error');
      return;
    }
    if (!petBase) return;
    const contactRef = doc(collection(db, petBase, 'contacts'));
    const optimistic = { id: contactRef.id, ...contact } as Contact;

    setContacts(prev => [...prev, optimistic]);

    setDoc(contactRef, stripUndefined(contact)).catch(err => {
      console.error('[DataContext] addContact failed:', err);
      if (!navigator.onLine) {
        enqueueWrites([{ id: contactRef.id, op: { type: 'set', path: contactRef.path, data: stripUndefined(contact) as Record<string, unknown> } }]);
      } else {
        setContacts(prev => prev.filter(c => c.id !== contactRef.id));
        showToast('Could not save contact. Please try again.', 'error');
      }
    });
  }, [isSubordinate, petBase, enqueueWrites, showToast]);

  const updateContact = useCallback(async (id: string, updates: Omit<Contact, 'id'>) => {
    if (isSubordinate) {
      showToast('Only the pet owner can edit contacts for this pet.', 'error');
      return;
    }
    if (!petBase) return;
    let previous: Contact | undefined;
    setContacts(prev => {
      previous = prev.find(c => c.id === id);
      return prev.map(c => c.id === id ? { ...c, ...updates } : c);
    });

    updateDoc(doc(db, petBase, 'contacts', id), stripUndefined(updates)).catch(err => {
      console.error('[DataContext] updateContact failed:', err);
      if (!navigator.onLine) {
        enqueueWrites([{ id: `upd:${petBase}/contacts/${id}`, op: { type: 'update', path: `${petBase}/contacts/${id}`, data: stripUndefined(updates) as Record<string, unknown> } }]);
      } else {
        if (previous) setContacts(prev => prev.map(c => c.id === id ? previous! : c));
        showToast('Could not update contact. Please try again.', 'error');
      }
    });
  }, [isSubordinate, petBase, enqueueWrites, showToast]);

  const deleteContact = useCallback(async (id: string) => {
    if (isSubordinate) {
      showToast('Only the pet owner can edit contacts for this pet.', 'error');
      return;
    }
    if (!petBase) return;
    let removed: Contact | undefined;
    setContacts(prev => {
      removed = prev.find(c => c.id === id);
      return prev.filter(c => c.id !== id);
    });

    deleteDoc(doc(db, petBase, 'contacts', id)).catch(err => {
      console.error('[DataContext] deleteContact failed:', err);
      if (!navigator.onLine) {
        enqueueWrites([{ id: `del:${petBase}/contacts/${id}`, op: { type: 'delete', path: `${petBase}/contacts/${id}` } }]);
      } else {
        if (removed) setContacts(prev => [...prev, removed!]);
        showToast('Could not delete contact. Please try again.', 'error');
      }
    });
  }, [isSubordinate, petBase, enqueueWrites, showToast]);

  // ── Document Vault ─────────────────────────────────────────────────────────

  const sanitizeFileName = (name: string) =>
    name
      .replace(/[^a-zA-Z0-9._-]/g, '_')
      .replace(/_+/g, '_')
      .slice(0, 120);

  const uploadDocument = useCallback(async (payload: {
    file: File;
    category: DocumentCategory;
    title?: string;
    note?: string;
    docDate?: string;
  }) => {
    if (isSubordinate) {
      showToast('Only the pet owner can upload documents for this pet.', 'error');
      return;
    }
    if (!uid || !petBase) return;

    const docRef = doc(collection(db, petBase, 'documents'));
    const cleanedName = sanitizeFileName(payload.file.name || 'document');
    const storagePath = `users/${uid}/pets/${activePetId}/documents/${docRef.id}/${cleanedName}`;
    const mimeType = payload.file.type || 'application/octet-stream';
    const createdAt = new Date().toISOString();

    // Read to ArrayBuffer up-front so we can store in IDB if the upload fails.
    const buffer = await payload.file.arrayBuffer();
    const storageRef = ref(storage, storagePath);

    try {
      await uploadBytes(storageRef, buffer, { contentType: mimeType });
      const downloadUrl = await getDownloadURL(storageRef);

      await setDoc(docRef, {
        user_id:      uid,
        category:     payload.category,
        file_name:    payload.file.name,
        file_size:    payload.file.size,
        mime_type:    mimeType,
        storage_path: storagePath,
        download_url: downloadUrl,
        title:        payload.title?.trim() || null,
        note:         payload.note?.trim()  || null,
        doc_date:     payload.docDate       || null,
        created_at:   createdAt,
      });

      // Reflect immediately in local state — don't wait for onSnapshot.
      const optimisticDoc: VaultDocument = {
        id:           docRef.id,
        user_id:      uid,
        category:     payload.category,
        file_name:    payload.file.name,
        file_size:    payload.file.size,
        mime_type:    mimeType,
        storage_path: storagePath,
        download_url: downloadUrl,
        title:        payload.title?.trim()  || undefined,
        note:         payload.note?.trim()   || undefined,
        doc_date:     payload.docDate        || undefined,
        created_at:   createdAt,
      };
      setDocuments(prev =>
        [optimisticDoc, ...prev].sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )
      );
    } catch (uploadErr) {
      // Upload failed (likely offline). Store in IDB and register Background Sync
      // so we can retry when connectivity is restored — even if the user closes the app.
      console.warn('[DataContext] uploadDocument failed — queuing for offline retry:', uploadErr);
      try {
        await enqueueUpload({
          id:             docRef.id,
          petBase,
          uid,
          activePetId:    activePetId!,
          firestoreDocId: docRef.id,
          storagePath,
          category:       payload.category,
          fileName:       payload.file.name,
          fileSize:       payload.file.size,
          mimeType,
          title:          payload.title?.trim()  || null,
          note:           payload.note?.trim()   || null,
          docDate:        payload.docDate        || null,
          buffer,
          enqueuedAt:     Date.now(),
        });
        setPendingUploadCount(prev => prev + 1);

        // Register Background Sync tag (Chrome/Edge — fires `sync` in the SW
        // even after the user has closed the tab).
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.ready
            .then(reg => { if ('sync' in reg) (reg as any).sync.register('koda-upload-retry').catch(() => {}); })
            .catch(() => {});
        }

        showToast(`"${payload.file.name}" queued — will upload when back online.`, 'info');
      } catch (queueErr) {
        console.error('[DataContext] Failed to queue upload for retry:', queueErr);
        showToast('Upload failed. Please check your connection and try again.', 'error');
      }
    }
  }, [isSubordinate, uid, petBase, activePetId, showToast]);

  const deleteDocument = useCallback(async (docMeta: VaultDocument) => {
    if (isSubordinate) {
      showToast('Only the pet owner can delete documents for this pet.', 'error');
      return;
    }
    if (!petBase) return;

    // Optimistic: remove from UI immediately.
    setDocuments(prev => prev.filter(d => d.id !== docMeta.id));

    // Background: delete storage object (non-critical) then the Firestore doc.
    const doDelete = async () => {
      try {
        await deleteObject(ref(storage, docMeta.storage_path));
      } catch (error) {
        console.warn('[DataContext] Failed to delete storage object:', error);
      }
      await deleteDoc(doc(db, petBase, 'documents', docMeta.id));
    };

    doDelete().catch(err => {
      console.error('[DataContext] deleteDocument failed:', err);
      setDocuments(prev =>
        [docMeta, ...prev].sort(
          (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        )
      );
      showToast('Could not delete document. Please try again.', 'error');
    });
  }, [isSubordinate, petBase, showToast]);

  const contextValue = useMemo(() => ({
    logs,
    medications,
    contacts,
    documents,
    pendingUploadCount,
    pendingWriteCount,
    addLog,
    updateLog,
    deleteLog,
    deleteLogs,
    addMedication,
    updateMedication,
    deleteMedication,
    addContact,
    updateContact,
    deleteContact,
    uploadDocument,
    deleteDocument,
  }), [
    logs, medications, contacts, documents, pendingUploadCount, pendingWriteCount,
    addLog, updateLog, deleteLog, deleteLogs,
    addMedication, updateMedication, deleteMedication,
    addContact, updateContact, deleteContact,
    uploadDocument, deleteDocument,
  ]);

  return (
    <DataContext.Provider value={contextValue}>
      {children}
      <ToastMount />
    </DataContext.Provider>
  );
}

export function useData() {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
}
